'use client';

/**
 * Client-side PostHog provider.
 *
 * Side-effect-only component (returns `null`) that owns the entire browser
 * PostHog story:
 *
 *   1. Boots `posthog-js` once via `initPostHogClient` from
 *      `@/lib/analytics/client`. That init call sets
 *      `capture_exceptions: true`, so any browser-side unhandled error
 *      (whether thrown in a render, an event handler, or a `setTimeout`)
 *      is automatically forwarded to the PostHog Errors product without
 *      us having to wire `window.onerror` manually.
 *   2. Identifies the current Supabase user with their profile so server-
 *      and client-emitted events land on the same person record.
 *   3. Emits a manual `$pageview` on every App Router navigation (the
 *      built-in listener only fires on the initial document load when the
 *      App Router is in play).
 *
 * Telemetry gating lives inside `initPostHogClient` — it short-circuits
 * unless `NODE_ENV === 'production'` or
 * `NEXT_PUBLIC_POSTHOG_FORCE_ENABLE === '1'`. So in local dev:
 *
 *   - `posthog.init()` is never called
 *   - `posthog.__loaded` stays `false`
 *   - `client-logger`, `global-error.tsx`, and `clientLogger` all fall
 *     back to console-only output
 *
 * `useSearchParams` requires Suspense; the layout wraps this component
 * accordingly.
 */

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  capturePageleave,
  capturePageview,
  identifyUser,
  initPostHogClient,
} from '@/lib/analytics/client';

// Anonymous landing-page visitors never need Supabase. We sniff for any
// `sb-*` cookie before dynamic-importing the client, so the ~100–150 KiB
// `@supabase/ssr` + `@supabase/supabase-js` bundle stays out of the public
// landing-page critical path. Authenticated users still get identified —
// the cookie sniff just gates whether we bother to load the SDK.
function hasSupabaseSessionCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.includes('sb-');
}

export function PostHogProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Boot once on mount. `initPostHogClient` is idempotent and gated.
  // In dev/test `initPostHogClient` resolves to `null`, so the early return
  // below already skips the Supabase round-trips. No extra env gate needed.
  useEffect(() => {
    let mounted = true;
    void initPostHogClient().then(async (ph) => {
      if (!ph || !mounted) return;
      if (!hasSupabaseSessionCookie()) return;

      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('users')
          .select('email, name, role, username, onboarding_completed')
          .eq('id', user.id)
          .maybeSingle();

        identifyUser(user.id, {
          ...(profile?.email && { email: profile.email, $email: profile.email }),
          ...(profile?.name && { name: profile.name, $name: profile.name }),
          ...(profile?.role && { role: profile.role }),
          ...(profile?.username && { username: profile.username }),
          ...(typeof profile?.onboarding_completed === 'boolean' && {
            onboarding_completed: profile.onboarding_completed,
          }),
        });
      } catch {
        // Telemetry must never break the app.
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Manual pageview/pageleave on route changes. App Router doesn't fire
  // `pagehide` between client-side nav, so posthog-js's built-in pageleave
  // listener won't see SPA transitions — we emit one for the URL we're
  // leaving before the pageview for the URL we're entering. On the very
  // first mount there's no previous URL, so we only emit the pageview.
  const previousUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pathname) return;
    const query = searchParams?.toString();
    const url = query ? `${pathname}?${query}` : pathname;

    const previousUrl = previousUrlRef.current;
    if (previousUrl && previousUrl !== url) {
      capturePageleave(previousUrl);
    }
    capturePageview(url);
    previousUrlRef.current = url;
  }, [pathname, searchParams]);

  return null;
}
